import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { obterProposta } from '../../services/propostaService';
import useAuth from '../../hooks/useAuth';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Button
} from '@mui/material';
import { Add as AddIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';

function PropostaDetails({ propostaId }) {
  const [proposta, setProposta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchProposta = async () => {
      if (!propostaId) return;
      try {
        setLoading(true);
        const response = await obterProposta(propostaId);
        setProposta(response);
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar proposta:', err);
        setError('Não foi possível carregar os detalhes da proposta.');
      } finally {
        setLoading(false);
      }
    };

    fetchProposta();
  }, [propostaId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!proposta) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Nenhum detalhe encontrado para esta proposta.
      </Alert>
    );
  }

  // Define a cor do chip de status
  const getStatusColor = (status) => {
    switch (status) {
      case 'APROVADA':
        return 'success';
      case 'REJEITADA':
        return 'error';
      default:
        return 'warning';
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1
      }}
    >
      {/* Título da proposta e status */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {proposta.titulo}
        </Typography>
        <Chip
          label={proposta.status}
          color={getStatusColor(proposta.status)}
          size="small"
        />
      </Box>

      {/* Datas de submissão e aprovação (se aprovada) */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Submetida em: {new Date(proposta.dataSubmissao).toLocaleDateString()}
        </Typography>
        {proposta.status === 'APROVADA' && (
          <Typography variant="body2" color="text.secondary">
            Aprovada em: {new Date(proposta.updated_at).toLocaleDateString()}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Problema de pesquisa */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Problema de Pesquisa
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            bgcolor: 'background.default'
          }}
        >
          <Typography variant="body1">
            {proposta.problemaPesquisa}
          </Typography>
        </Paper>
      </Box>

      {/* Se existir metodologia */}
      {proposta.metodologia && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            Metodologia
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: 'background.default'
            }}
          >
            <Typography variant="body1">
              {proposta.metodologia}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Se existir objetivos */}
      {proposta.objetivos && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            Objetivos
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: 'background.default'
            }}
          >
            <Typography variant="body1">
              {proposta.objetivos}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Se foi rejeitada, exibe o motivo */}
      {proposta.status === 'REJEITADA' && proposta.motivoRejeicao && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="error">
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Motivo da Rejeição:
            </Typography>
            <Typography variant="body2">
              {proposta.motivoRejeicao}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Se foi aprovada, exibe as observações da aprovação (se houver) */}
      {proposta.status === 'APROVADA' && proposta.observacoes && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Observações:</Typography>
          <Typography variant="body2">{proposta.observacoes}</Typography>
        </Alert>
      )}

      {/* Botões de ação */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        {/* Só o estudante pode submeter projeto */}
        {user?.tipo === 'estudante' && proposta.status === 'APROVADA' && !proposta.projeto && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/projetos/submeter?propostaId=${proposta.id}`)}
          >
            Submeter Projeto
          </Button>
        )}
        <Button
          variant="outlined"
          endIcon={<ChevronRightIcon />}
          onClick={() => navigate(`/propostas/${proposta.id}`)}
        >
          Ver Detalhes
        </Button>
      </Box>
    </Paper>
  );
}

PropostaDetails.propTypes = {
  propostaId: PropTypes.string.isRequired
};

export default PropostaDetails;
